import { useRef, useState } from 'react';
import { Share2 } from 'lucide-react';

// Compartilhar um anexo do chat pra FORA do app.
//
// ⚠️ Nada aqui passa pelo servidor da loja nem pelo WhatsApp da loja ([DA-523]):
// quem abre e' o WhatsApp do proprio cliente, no celular dele, e o destinatario e'
// quem ele escolher. Cair o Baileys nao afeta este botao em nada.
//
// **Dois caminhos, um botao so.**
//
// 1. `navigator.share` com o ARQUIVO — abre o seletor do proprio aparelho (WhatsApp,
//    Telegram, e-mail, salvar) e o que chega no destinatario e' a foto de verdade.
// 2. Quando o aparelho nao sabe compartilhar arquivo (PC, navegador antigo), abre o
//    WhatsApp com o LINK do anexo no texto. **Isso e' um limite da plataforma, nao
//    uma escolha**: a FAQ oficial do "clique para conversa" documenta um unico
//    parametro na URL — `text` — e nenhum de arquivo. Por esse caminho e' impossivel
//    mandar a imagem anexada; o que vai e' um endereco que o destinatario toca.
//
// ⚠️ **O arquivo comeca a ser baixado no TOQUE, nao no clique.** O Safari do iPhone
// so aceita `navigator.share` enquanto durar o gesto do usuario; um `await fetch` no
// meio do caminho gasta essa janela e a chamada volta `NotAllowedError` com a cara de
// defeito aleatorio. Baixando antes, o clique ja encontra o arquivo pronto.
//
// ⚠️ **Cancelar nao e' falhar.** Se o cliente abre o seletor e desiste, o navegador
// devolve `AbortError` — abrir o WhatsApp em seguida seria insistir num envio que ele
// acabou de recusar.

const WHATSAPP_TEXTO = 'https://wa.me/?text=';

// O nome do arquivo no servidor e' `<timestamp>-outbound.jpg` — nome de maquina, que
// aparece inteiro na tela de compartilhar E chega assim em quem recebe. Quem abre no
// celular de um terceiro merece ler de onde aquilo veio, nao um numero de 13 digitos.
function nomeDoArquivo(url: string, criadoEm?: string): string {
  const semQuery = url.split('?')[0] ?? url;
  const original = semQuery.split('/').pop() ?? '';
  const ponto = original.lastIndexOf('.');
  // Sem extensao o iPhone nao sabe que e' foto — na duvida, preserva o nome de origem.
  if (ponto <= 0) return original.length > 0 ? original : 'fio-de-cobre';
  const extensao = original.slice(ponto);

  const d = criadoEm ? new Date(criadoEm) : null;
  if (!d || Number.isNaN(d.getTime())) return `fio-de-cobre${extensao}`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `fio-de-cobre-${dd}-${mm}-${hh}h${mi}${extensao}`;
}

interface Props {
  url: string;
  /** Vem do banco; quando falta, o proprio download responde pelo `blob.type`. */
  mime?: string | null;
  /** Data da mensagem — vira o nome do arquivo que o destinatario recebe. */
  criadoEm?: string;
  /**
   * Nome de verdade do anexo, quando existe (documento: `midia_nome`).
   *
   * ⚠️ Foto nao tem nome nenhum que valha a pena — o do servidor e' `<timestamp>-outbound.jpg`,
   * e por isso o `nomeDoArquivo` inventa um legivel. **Documento e' o oposto**: o nome
   * escolhido por quem anexou ("Extrato - ILUMINAR.pdf") e' a unica coisa que diz o que
   * aquilo e', e renomear pra `fio-de-cobre-18-08-10h45.pdf` faria o cliente receber um
   * arquivo anonimo ([DA-572]).
   */
  nome?: string | null;
  rotulo: string;
  /**
   * `sobreposto` e' a versao clara pra cima da foto em tela cheia; `linha-de-menu`
   * e' a linha de largura inteira dentro da folha de opcoes da mensagem ([DA-573]).
   */
  variante?: 'discreto' | 'sobreposto' | 'linha-de-menu';
  /**
   * Chamado DEPOIS de disparar o compartilhamento — e' assim que a folha de opcoes
   * se fecha sozinha.
   *
   * ⚠️ **A ordem importa e nao e' cosmetica**: `compartilhar()` roda o trecho
   * sincrono ANTES de qualquer `await` (e' la que o `window.open` do fallback mora,
   * pelo p.6 da [DA-524]), entao ele e' disparado primeiro e o fechamento vem
   * depois. Fechar antes tiraria o `window.open` de dentro do clique e o bloqueador
   * de pop-up entraria no meio.
   */
  aoAcionar?: () => void;
}

const COBRE = '#b45309';

export function BotaoCompartilhar({ url, mime, criadoEm, nome, rotulo, variante = 'discreto', aoAcionar }: Props) {
  const [ocupado, setOcupado] = useState(false);
  // Uma promessa por botao: o toque dispara o download e o clique reaproveita.
  const arquivoRef = useRef<Promise<File | null> | null>(null);

  function baixar(): Promise<File | null> {
    if (!arquivoRef.current) {
      arquivoRef.current = (async () => {
        try {
          const resp = await fetch(url);
          if (!resp.ok) return null;
          const blob = await resp.blob();
          const tipo = mime || blob.type || 'application/octet-stream';
          return new File([blob], (nome ?? '').trim() || nomeDoArquivo(url, criadoEm), { type: tipo });
        } catch {
          return null;
        }
      })();
    }
    return arquivoRef.current;
  }

  function abrirWhatsApp() {
    // O caminho do anexo vem relativo do banco; o WhatsApp precisa do endereco inteiro.
    const link = WHATSAPP_TEXTO + encodeURIComponent(new URL(url, window.location.origin).href);
    // Aba nova mantem o app aberto atras. Bloqueador de pop-up devolve `null` — e ai
    // navegar a propria aba e' o que sobra (o app volta do jeito que estava).
    //
    // ⛔ **Nao repor o `'noopener'` como 3o argumento**: com ele o `window.open` devolve
    // `null` SEMPRE (especificacao — nao ha referencia pra devolver), o `if` abaixo
    // dispara a cada toque e o WhatsApp abre DUAS vezes: numa aba nova **e** por cima do
    // proprio app. Ficou assim de 13/08 ate ser medido em 18/08 ([DA-572]) — o codigo le
    // como correto, e nenhum portao aperta botao. O `noopener` vem logo abaixo, na mao.
    const aba = window.open(link, '_blank');
    if (!aba) {
      window.location.href = link;
      return;
    }
    aba.opener = null;
  }

  async function compartilhar() {
    if (ocupado) return;
    const nav = navigator as Navigator & {
      share?: (dados: { files?: File[] }) => Promise<void>;
      canShare?: (dados: { files?: File[] }) => boolean;
    };

    // Sem suporte a arquivo, o WhatsApp abre AGORA, ainda dentro do clique — decidir
    // isso depois de um `await` e' o que faz o bloqueador de pop-up entrar no meio.
    if (!nav.share || !nav.canShare) {
      abrirWhatsApp();
      return;
    }

    setOcupado(true);
    try {
      const arquivo = await baixar();
      if (arquivo && nav.canShare({ files: [arquivo] })) {
        try {
          await nav.share({ files: [arquivo] });
          return;
        } catch (e) {
          if ((e as Error | undefined)?.name === 'AbortError') return;
        }
      }
      abrirWhatsApp();
    } finally {
      setOcupado(false);
    }
  }

  // Dentro da folha de opcoes o botao deixa de ser icone e passa a ser LINHA com
  // texto — quem abriu a folha esta lendo palavras, nao adivinhando desenhos. O
  // miolo (baixar no toque, compartilhar no clique) e' o mesmo dos outros dois.
  if (variante === 'linha-de-menu') {
    return (
      <button
        type="button"
        onPointerDown={(e) => { e.stopPropagation(); void baixar(); }}
        onClick={(e) => { e.stopPropagation(); void compartilhar(); aoAcionar?.(); }}
        disabled={ocupado}
        style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '15px 14px', fontSize: 16, color: '#111',
          borderRadius: 12, textAlign: 'left', width: '100%',
          opacity: ocupado ? 0.5 : 1,
        }}
      >
        <Share2 size={22} color={COBRE} /> {rotulo}
      </button>
    );
  }

  const sobreposto = variante === 'sobreposto';

  return (
    <button
      type="button"
      onPointerDown={(e) => { e.stopPropagation(); void baixar(); }}
      onClick={(e) => { e.stopPropagation(); void compartilhar(); aoAcionar?.(); }}
      aria-label={rotulo}
      title={rotulo}
      style={{
        width: sobreposto ? 44 : 30,
        height: sobreposto ? 44 : 30,
        flexShrink: 0,
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: sobreposto ? '#fff' : '#a8a29e',
        backgroundColor: sobreposto ? 'rgba(255,255,255,0.16)' : 'transparent',
        opacity: ocupado ? 0.5 : 1,
      }}
    >
      <Share2 size={sobreposto ? 20 : 16} />
    </button>
  );
}
